import { morph } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  /**
   * Flag to track if current render is from filter selection
   * @type {boolean}
   */
  #isFilterRender = false;

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
    
    // Listen for clicks inside dialog-component to mark as filter render
    document.addEventListener('click', (event) => {
      if (!event.target || !(event.target instanceof Element)) return;
      
      const dialogComponent = event.target.closest('dialog-component#custom-filter-drawer');
      if (dialogComponent) {
        this.#isFilterRender = true;
      } else {
        // Clicked outside dialog, reset flag
        this.#isFilterRender = false;
      }
    }, true); // Use capture phase to catch early
  }

  /**
   * Mark the next render as a filter render
   */
  markAsFilterRender() {
    this.#isFilterRender = true;
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};

    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);

      // Pre-apply opening state to dialog if this is a filter render
      let processedHTML = sectionHTML;
      if (this.#isFilterRender) {
        // Only preserve/open the drawer if it was already open (or should stay open).
        // Otherwise, filtering via quick pills should NOT force the drawer to appear.
        const shouldStayOpen = Boolean(/** @type {any} */ (window).__customFilterState?.shouldStayOpen);
        const currentDialog = document.querySelector('#custom-filter-drawer dialog');
        const isCurrentlyOpen = Boolean(currentDialog && (currentDialog.hasAttribute('open') || /** @type {any} */ (currentDialog).open));

        if (shouldStayOpen || isCurrentlyOpen) {
          processedHTML = this.#prepareDialogForFilterRender(sectionHTML);
        }
      }

      morphSection(sectionId, processedHTML);
      
      // If this render was triggered by filtering, emit a completion event
      // after morphing so other scripts can re-initialize safely.
      if (this.#isFilterRender) {
        try {
          document.dispatchEvent(new Event('shopify:filter:updated'));
        } catch (e) {}
        this.#isFilterRender = false;
      }
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => {
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Prepares dialog component for filter render to prevent flicker
   * @param {string} sectionHTML - The new section HTML
   * @returns {string} Modified HTML with opening state applied
   */
  #prepareDialogForFilterRender(sectionHTML) {
    // Parse the new HTML into a DOM fragment
    const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newDialogComponent = fragment.querySelector('dialog-component#custom-filter-drawer');
    
    if (!newDialogComponent) {
      return sectionHTML;
    }

    // Find the dialog inside the new dialog-component
    const newDialog = newDialogComponent.querySelector('.custom-filter-dialog');
    
    if (newDialog) {
      // Add opening class to the new dialog
      newDialog.classList.add('opening');
      
      // Set display block on the new dialog
      if (newDialog instanceof HTMLElement) {
        newDialog.style.display = 'block';
      }
      
      // Add opening class to the new dialog-component wrapper
      newDialogComponent.classList.add('opening');
      
      // Make sure the new dialog-component is visible
      if (newDialogComponent instanceof HTMLElement) {
        newDialogComponent.style.display = 'block';
      }
      
      // Restore active filter label state in the NEW HTML
      const savedActiveFilterParam = /** @type {any} */ (window).__customFilterState?.activeFilterParam;
      if (savedActiveFilterParam) {
        // Ensure filter values container is visible
        const filterValuesContainer = fragment.querySelector('.filter-values-container');
        if (filterValuesContainer instanceof HTMLElement) {
          filterValuesContainer.style.display = 'block';
        }
        
        // Find all filter label buttons in the new HTML
        const filterButtons = fragment.querySelectorAll('.filter-label-button');
        filterButtons.forEach(button => {
          const filterParam = button.getAttribute('data-filter-param');
          
          if (filterParam === savedActiveFilterParam) {
            // Add active class to the matching button in NEW HTML
            button.classList.add('active');
          } else {
            // Remove active class from other buttons in NEW HTML
            button.classList.remove('active');
          }
        });
        
        // Hide all filter value lists first, then show only the active one
        const allFilterLists = fragment.querySelectorAll('.filter-values-list');
        allFilterLists.forEach(list => {
          const listParam = list.getAttribute('data-filter-param');
          
          if (listParam === savedActiveFilterParam) {
            // Show the matching filter values list
            list.classList.add('active');
            if (list instanceof HTMLElement) {
              list.style.display = 'block';
            }
          } else {
            // Hide other filter value lists
            list.classList.remove('active');
            if (list instanceof HTMLElement) {
              list.style.display = 'none';
            }
          }
        });
      }
      
      // Restore checked state of checkboxes based on current URL
      const currentUrl = new URL(window.location.href);
      const urlParams = currentUrl.searchParams;
      
      // Get all checkboxes in the new HTML
      const allCheckboxes = fragment.querySelectorAll('.filter-value-checkbox');
      let restoredCheckboxCount = 0;
      
      allCheckboxes.forEach(checkbox => {
        const checkboxName = checkbox.getAttribute('name');
        const checkboxValue = checkbox.getAttribute('value');
        
        if (checkboxName && checkboxValue) {
          // Check if this parameter exists in the URL
          const urlValues = urlParams.getAll(checkboxName);
          
          if (urlValues.includes(checkboxValue)) {
            // This checkbox should be checked
            checkbox.setAttribute('checked', '');
            if (checkbox instanceof HTMLInputElement) {
              checkbox.checked = true;
            }
            restoredCheckboxCount++;
          } else {
            // Ensure checkbox is unchecked
            checkbox.removeAttribute('checked');
            if (checkbox instanceof HTMLInputElement) {
              checkbox.checked = false;
            }
          }
        }
      });
      
      // Restore price tab active state based on current URL
      const priceGte = urlParams.get("filter.v.price.gte");
      const priceLte = urlParams.get("filter.v.price.lte");
      
      if (priceGte && priceLte) {
        const minPrice = parseInt(priceGte);
        const maxPrice = parseInt(priceLte);
        
        // Find all price tabs in the new HTML
        const priceTabs = fragment.querySelectorAll('.price-value-tab');
        let matchingTabFound = false;
        
        priceTabs.forEach(tab => {
          const tabMinValue = parseInt(tab.getAttribute('min-value') || '0');
          const tabMaxValue = parseInt(tab.getAttribute('max-value') || '0');
          
          // Check if this tab matches the URL parameters
          if (tabMinValue === minPrice && tabMaxValue === maxPrice) {
            tab.classList.add('active');
            matchingTabFound = true;
          } else {
            tab.classList.remove('active');
          }
        });
      } else {
        // No price filter in URL, ensure all price tabs are inactive
        const priceTabs = fragment.querySelectorAll('.price-value-tab');
        priceTabs.forEach(tab => {
          tab.classList.remove('active');
        });
      }
      
      // Set a flag to prevent getActivePrice from running after morph
      /** @type {any} */ (window).__skipGetActivePrice = true;
      
      // Set a flag to prevent showFilterValues from running after morph
      /** @type {any} */ (window).__skipShowFilterValues = true;
      
      // Set a flag to prevent refreshFilterValues from running after morph
      // (it's redundant since we already have fresh HTML with updated counts)
      /** @type {any} */ (window).__skipRefreshFilterValues = true;
    }
    
    // Serialize the modified fragment back to HTML string
    const modifiedHTML = fragment.documentElement.outerHTML;
    return modifiedHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);
}

export const sectionRenderer = new SectionRenderer();
